import {
	Box,
	Button,
	Flex,
	Heading,
	Input,
	Radio,
	RadioGroup,
	Select,
	Stack,
	Text
} from "@chakra-ui/react";
import React, {useLayoutEffect, useMemo, useRef} from "react";
import {
	AREA_LANGUAGE_DOWNLOAD_ALL,
	AREA_LANGUAGE_DOWNLOAD_DEFAULT_ONLY,
	AREA_LANGUAGE_DOWNLOAD_USER_CHOOSE,
	AreaLanguageDownloadMode,
	AreaLanguageOption
} from "../../types";

type LanguageSettingsTabProps = {
	canEdit: boolean;
	canUseExtendedLanguages: boolean;
	defaultLanguage: string;
	languageDownloadMode: AreaLanguageDownloadMode;
	languageToAdd: string;
	configuredLanguages: AreaLanguageOption[];
	availableLanguageCandidates: AreaLanguageOption[];
	onLanguageDownloadModeChange: (value: AreaLanguageDownloadMode) => void;
	onLanguageToAddChange: (value: string) => void;
	onAddLanguage: () => void;
	onDefaultChange: (value: string) => void;
	onRemoveLanguage: (code: string) => void;
	onLanguageLabelChange: (code: string, label: string) => void;
	onLanguageFlagChange: (code: string, flagUrl: string) => void;
	onUseSuggestedFlag: (code: string) => void;
	getSuggestedFlagUrl: (code: string) => string | undefined;
};

const LanguageSettingsTab = ({
	canEdit,
	canUseExtendedLanguages,
	defaultLanguage,
	languageDownloadMode,
	languageToAdd,
	configuredLanguages,
	availableLanguageCandidates,
	onLanguageDownloadModeChange,
	onLanguageToAddChange,
	onAddLanguage,
	onDefaultChange,
	onRemoveLanguage,
	onLanguageLabelChange,
	onLanguageFlagChange,
	onUseSuggestedFlag,
	getSuggestedFlagUrl
}: LanguageSettingsTabProps) => {
	const cardRefs = useRef<Record<string, HTMLDivElement | null>>({});
	const previousCardTops = useRef<Map<string, number>>(new Map());

	const sortedConfiguredLanguages = useMemo(() => {
		const normalizedDefault = defaultLanguage.toUpperCase();
		return [...configuredLanguages].sort((a, b) => {
			const aCode = (a.code || "").toUpperCase();
			const bCode = (b.code || "").toUpperCase();
			const aIsDefault = aCode === normalizedDefault;
			const bIsDefault = bCode === normalizedDefault;
			if (aIsDefault && !bIsDefault) {
				return -1;
			}
			if (!aIsDefault && bIsDefault) {
				return 1;
			}
			return aCode.localeCompare(bCode);
		});
	}, [configuredLanguages, defaultLanguage]);

	const sortedAvailableLanguageCandidates = useMemo(() => {
		return [...availableLanguageCandidates]
			.sort((a, b) => (a.code || "").toUpperCase().localeCompare((b.code || "").toUpperCase()));
	}, [availableLanguageCandidates]);

	useLayoutEffect(() => {
		const nextCardTops = new Map<string, number>();
		sortedConfiguredLanguages.forEach((language) => {
			const element = cardRefs.current[language.code];
			if (element) {
				nextCardTops.set(language.code, element.getBoundingClientRect().top);
			}
		});

		if (previousCardTops.current.size === 0) {
			previousCardTops.current = nextCardTops;
			return;
		}

		sortedConfiguredLanguages.forEach((language) => {
			const element = cardRefs.current[language.code];
			if (!element) {
				return;
			}
			const previousTop = previousCardTops.current.get(language.code);
			const nextTop = nextCardTops.get(language.code);
			if (previousTop === undefined || nextTop === undefined) {
				return;
			}
			const deltaY = previousTop - nextTop;
			if (Math.abs(deltaY) < 1) {
				return;
			}
			element.style.transition = "transform 0ms";
			element.style.transform = `translateY(${deltaY}px)`;
			requestAnimationFrame(() => {
				element.style.transition = "transform 220ms ease";
				element.style.transform = "translateY(0)";
			});
		});

		previousCardTops.current = nextCardTops;
	}, [sortedConfiguredLanguages]);

	return (
		<Stack spacing={6}>
			<Box>
				<Heading size={"sm"} mb={2}>Download</Heading>
				<Text fontSize={"sm"} color={"gray.600"} mb={4}>
					Languages to download when loading the space
				</Text>
				<RadioGroup
					value={languageDownloadMode}
					onChange={(value) => onLanguageDownloadModeChange(value as AreaLanguageDownloadMode)}
				>
					<Stack spacing={2}>
						<Radio value={AREA_LANGUAGE_DOWNLOAD_ALL} isDisabled={!canEdit}>All</Radio>
						<Radio value={AREA_LANGUAGE_DOWNLOAD_DEFAULT_ONLY} isDisabled={!canEdit}>Default only</Radio>
						<Radio value={AREA_LANGUAGE_DOWNLOAD_USER_CHOOSE} isDisabled={!canEdit}>Let user choose</Radio>
					</Stack>
				</RadioGroup>
				<Text fontSize={"sm"} color={"gray.600"} mt={3}>
					Choosing &quot;Default only&quot; or &quot;Let user choose&quot; uses less data and time to download. Choose &quot;All&quot; for offline use.
				</Text>
			</Box>
			<Box>
				<Heading size={"sm"} mb={2}>Languages</Heading>
				<Text fontSize={"sm"} color={"gray.600"}>
					Add languages, customize labels and SVG flags, and choose the default language.
				</Text>
				{canUseExtendedLanguages ? null : (
					<Text fontSize={"sm"} color={"gray.600"} mt={2}>
						This organization is limited to Danish and English language options.
					</Text>
				)}
			</Box>
			<Stack spacing={3}>
				<Heading size={"xs"} color={"gray.700"}>Add language</Heading>
				<Flex gap={3} align={"center"} wrap={"wrap"}>
					<Box minW={"260px"} maxW={"420px"} flex={1}>
						<Select
							value={languageToAdd}
							onChange={(event) => onLanguageToAddChange(event.target.value)}
							isDisabled={!canEdit || availableLanguageCandidates.length === 0}
						>
							{sortedAvailableLanguageCandidates.map((language) => (
								<option key={language.code} value={language.code}>
									{language.label} ({language.code})
								</option>
							))}
						</Select>
					</Box>
					<Button
						size={"sm"}
						onClick={onAddLanguage}
						isDisabled={!canEdit || !languageToAdd}
					>
						Add
					</Button>
				</Flex>
				{availableLanguageCandidates.length === 0 ? (
					<Text fontSize={"sm"} color={"gray.600"}>
						All language candidates are already added.
					</Text>
				) : null}
			</Stack>
			<Box borderWidth={"1px"} borderColor={"gray.200"} borderRadius={"lg"} px={4} py={3}>
				<RadioGroup value={defaultLanguage} onChange={onDefaultChange}>
					<Stack spacing={4}>
						{sortedConfiguredLanguages.map((language) => {
							const suggestedFlag = getSuggestedFlagUrl(language.code);
							return (
								<Box
									key={language.code}
									ref={(element) => {
										cardRefs.current[language.code] = element;
									}}
									borderWidth={"1px"}
									borderColor={"gray.100"}
									borderRadius={"md"}
									p={3}
									willChange={"transform"}
								>
									<Flex justify={"space-between"} align={"center"} mb={3}>
										<Text fontWeight={600}>{language.code}</Text>
										<Flex align={"center"} gap={3}>
											<Radio value={language.code}>Default</Radio>
											<Button
												size={"xs"}
												variant={"outline"}
												colorScheme={"red"}
												onClick={() => onRemoveLanguage(language.code)}
												isDisabled={!canEdit}
											>
												Remove
											</Button>
										</Flex>
									</Flex>
									<Stack spacing={3}>
										<Box>
											<Text fontSize={"xs"} color={"gray.600"} mb={1}>Label</Text>
											<Input
												value={language.label}
												onChange={(event) => onLanguageLabelChange(language.code, event.target.value)}
												isDisabled={!canEdit}
											/>
										</Box>
										<Box>
											<Text fontSize={"xs"} color={"gray.600"} mb={1}>Flag SVG URL</Text>
											<Flex gap={2} wrap={"wrap"}>
												<Input
													value={language.flagUrl || ""}
													onChange={(event) => onLanguageFlagChange(language.code, event.target.value)}
													isDisabled={!canEdit}
													placeholder={"https://.../flag.svg"}
												/>
												<Button
													size={"xs"}
													variant={"outline"}
													onClick={() => onUseSuggestedFlag(language.code)}
													isDisabled={!canEdit || !suggestedFlag}
												>
													Use suggested
												</Button>
											</Flex>
											<Flex mt={2} gap={4} wrap={"wrap"} align={"center"}>
												<Flex align={"center"} gap={2}>
													<Text fontSize={"xs"} color={"gray.600"}>Selected flag:</Text>
													{language.flagUrl ? (
														<Box
															as={"img"}
															src={language.flagUrl}
															alt={`${language.code} selected flag`}
															boxSize={"28px"}
															borderWidth={"1px"}
															borderColor={"gray.200"}
															borderRadius={"sm"}
														/>
													) : (
														<Text fontSize={"xs"} color={"gray.500"}>none</Text>
													)}
												</Flex>
												<Flex align={"center"} gap={2}>
													<Text fontSize={"xs"} color={"gray.600"}>Suggested flag:</Text>
													{suggestedFlag ? (
														<Box
															as={"img"}
															src={suggestedFlag}
															alt={`${language.code} suggested flag`}
															boxSize={"28px"}
															borderWidth={"1px"}
															borderColor={"gray.200"}
															borderRadius={"sm"}
														/>
													) : (
														<Text fontSize={"xs"} color={"gray.500"}>not available</Text>
													)}
												</Flex>
											</Flex>
										</Box>
									</Stack>
								</Box>
							);
						})}
					</Stack>
				</RadioGroup>
			</Box>
			{configuredLanguages.length === 0 ? (
				<Text fontSize={"sm"} color={"gray.600"}>
					No languages selected. English will be used as the default language.
				</Text>
			) : null}
		</Stack>
	);
};

export default LanguageSettingsTab;
